import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "apollo-react/components/Tooltip";
import Select from "apollo-react/components/Select";
import MenuItem from "apollo-react/components/MenuItem";
import { usePostmongerContext } from "../../../contexts/PostmongerContext";
import { isInArgumentFieldValid } from "../../common/helpers";
import { fieldNames } from "../../common/constants";

const WebinarDetails = (props, ref) => {

  const { t } = useTranslation();
  const {
    inArguments,
    setDirty,
    seedData,
    disabled,
    getWebinars,
    getWebinarOccurrences,
  } = usePostmongerContext();

  const defaultWebinarConnectionId = () =>
    inArguments.get(fieldNames.webinarConnectionId) || "";
  const [webinarConnectionId, setWebinarConnectionId] = useState(
    defaultWebinarConnectionId()
  );

  const defaultWebinarId = () => inArguments.get(fieldNames.webinarId) || "";
  const [webinarId, setWebinarId] = useState(defaultWebinarId());

  const defaultOccurrenceId = () =>
    inArguments.get(fieldNames.occurrenceId) || "";
  const [occurrenceId, setoccurrenceId] = useState(defaultOccurrenceId());

  const setValueToField = (value, fieldName, callBackFunction) => {
    callBackFunction(value);
    inArguments.set(fieldName, value);
  };

  const onChangeWebinarConnection = (event) => {
    const value = event.target.value;
    setValueToField(
      value,
      fieldNames.webinarConnectionId,
      setWebinarConnectionId
    );
    setDirty();
    getWebinars(value);
    setValueToField("", fieldNames.webinarId, setWebinarId);
    setValueToField("", fieldNames.occurrenceId, setoccurrenceId);
  };

  const onChangeWebinarName = (event) => {
    const value = event.target.value;
    setValueToField(value, fieldNames.webinarId, setWebinarId);
    setDirty();
    const conn = inArguments.get(fieldNames.webinarConnectionId);
    getWebinarOccurrences(conn, value);
    setValueToField("", fieldNames.occurrenceId, setoccurrenceId);
  };

  const onChangeOccurrenceDateTime = (event) => {
    const value = event.target.value;
    setValueToField(value, fieldNames.occurrenceId, setoccurrenceId);
    setDirty();
    setRegistrationURL(value);
  };

  const setRegistrationURL = (webinarEventId) => {
    var webinars = seedData.Occurrences.filter((obj) => {
      return obj.id === webinarEventId;
    });
    var eventURL = webinars[0].registrationUrl ? webinars[0].registrationUrl : webinars[0].joinUrl;
    inArguments.set(fieldNames.registrationUrl, eventURL);
  };

  const GetWebinarConnectionsMenuItems = () => {
    var connectionItems = [];
    var connections = seedData.Connections;
    if (connections) {
      for (let item of connections) {
        connectionItems.push(
          <MenuItem key={item.connectionId} value={item.connectionId}>
            {item.connectionName}
          </MenuItem>
        );
      }
    }
    return connectionItems;
  };

  const GetWebinarNameMenuItems = () => {
    var webinarItems = [];
    var webinars = seedData.Webinars;
    if (webinars) {
      for (let item of webinars) {
        webinarItems.push(
          <MenuItem key={item.webinarId} value={item.webinarId}>
            {item.webinarName}
          </MenuItem>
        );
      }
    }
    return webinarItems;
  };

  const GetOccurrenceDateTimeMenuItems = () => {
    var occurrenceItems = [];
    var occurrences = seedData.Occurrences;
    if (occurrences) {
      for (let item of occurrences) {
        let displayTime =
          item.startTime && item.timeZone
            ? `${item.startTime} (${item.timeZone})`
            : item.startTime
            ? item.startTime
            : "";
        occurrenceItems.push(
          <MenuItem key={item.id} value={item.id}>
            {displayTime}
          </MenuItem>
        );
      }
    }
    return occurrenceItems;
  };

  const [webinarConnectionsMenuItems, setWebinarConnectionsMenuItems] =
    useState(GetWebinarConnectionsMenuItems());

  const [webinarNameMenuItems, setWebinarNameMenuItems] = useState(
    GetWebinarNameMenuItems()
  );

  const [occurrenceDateTimeMenuItems, setOccurrenceDateTimeMenuItems] =
    useState(GetOccurrenceDateTimeMenuItems());

  useEffect(() => {
    setWebinarConnectionsMenuItems(GetWebinarConnectionsMenuItems());
    setWebinarNameMenuItems(GetWebinarNameMenuItems());
    setOccurrenceDateTimeMenuItems(GetOccurrenceDateTimeMenuItems());
  }, [seedData]);

  return (
    <div  style={{marginBottom: 32, padding: 0}}>
      <Tooltip title={t(fieldNames.webinarConnectionLabel)}>
        <Select
          label={t(fieldNames.webinarConnectionLabel)}
          fullWidth
          required
          onChange={onChangeWebinarConnection}
          placeholder={t(fieldNames.webinarConnectionLabel)}
          disabled={disabled}
          value={webinarConnectionId}
          name={fieldNames.webinarConnectionId}
          data-testid={fieldNames.webinarConnectionId}
          error={
            !isInArgumentFieldValid(inArguments, fieldNames.webinarConnectionId)
          }
        >
          {webinarConnectionsMenuItems}
        </Select>
      </Tooltip>
      <Tooltip title={t(fieldNames.webinarNameLabel)}>
        <Select
          label={t(fieldNames.webinarNameLabel)}
          fullWidth
          required
          onChange={onChangeWebinarName}
          placeholder={t(fieldNames.webinarNameLabel)}
          disabled={disabled}
          value={webinarId}
          name={fieldNames.webinarId}
          data-testid={fieldNames.webinarId}
          error={!isInArgumentFieldValid(inArguments, fieldNames.webinarId)}
        >
          {webinarNameMenuItems}
        </Select>
      </Tooltip>
      <Tooltip title={t(fieldNames.webinarOccurrenceDateTimeLabel)}>
        <Select
          label={t(fieldNames.webinarOccurrenceDateTimeLabel)}
          fullWidth
          required
          onChange={onChangeOccurrenceDateTime}
          placeholder={t(fieldNames.webinarOccurrenceDateTimeLabel)}
          disabled={disabled}
          value={occurrenceId}
          name={fieldNames.occurrenceId}
          data-testid={fieldNames.occurrenceId}
          error={!isInArgumentFieldValid(inArguments, fieldNames.occurrenceId)}
        >
          {occurrenceDateTimeMenuItems}
        </Select>
      </Tooltip>
    </div>
  );
};

export default forwardRef(WebinarDetails);
